import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type PomodoroMode = 'work' | 'short_break' | 'long_break'
export type PomodoroStatus = 'idle' | 'running' | 'paused'

export interface PomodoroTimerSettings {
  workDuration: number
  shortBreakDuration: number
  longBreakDuration: number
  sessionsBeforeLongBreak: number
  autoStartBreaks: boolean
  autoStartWork: boolean
  timerStyle: 'circular' | 'flat'
  backgroundType: 'color' | 'image' | 'gif' | 'video'
  backgroundValue: string
  soundEnabled: boolean
  soundType: 'tick' | 'rain' | 'fire' | 'mp3'
  soundUrl: string | null
  alarmSound: 'bell' | 'digital' | 'gentle' | 'none'
  volume: number
}

const DEFAULT_SETTINGS: PomodoroTimerSettings = {
  workDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  sessionsBeforeLongBreak: 4,
  autoStartBreaks: false,
  autoStartWork: false,
  timerStyle: 'circular',
  backgroundType: 'color',
  backgroundValue: '#BA4949',
  soundEnabled: false,
  soundType: 'tick',
  soundUrl: null,
  alarmSound: 'bell',
  volume: 80,
}

interface PomodoroState {
  mode: PomodoroMode
  status: PomodoroStatus
  timeRemaining: number
  endTime: number | null
  currentSession: number
  startedAt: string | null
  activeHabitId: string | null
  activeHabitName: string | null
  activeTaskId: string | null
  activeTaskText: string | null
  settings: PomodoroTimerSettings
  isSettingsOpen: boolean
  isBgPickerOpen: boolean
  isSoundPickerOpen: boolean
  isPipActive: boolean

  startTimer: () => void
  pauseTimer: () => void
  resumeTimer: () => void
  skipToNext: () => void
  resetAll: () => void
  tick: () => void
  recoverFromBackground: () => boolean
  setMode: (mode: PomodoroMode) => void
  setActiveHabit: (id: string | null, name: string | null) => void
  setActiveTask: (id: string | null, text: string | null) => void
  updateSettings: (partial: Partial<PomodoroTimerSettings>) => void
  setSettingsOpen: (open: boolean) => void
  setBgPickerOpen: (open: boolean) => void
  setSoundPickerOpen: (open: boolean) => void
  setPipActive: (active: boolean) => void
  getDurationForMode: (mode: PomodoroMode) => number
}

function getDuration(mode: PomodoroMode, s: PomodoroTimerSettings): number {
  if (mode === 'work') return s.workDuration * 60
  if (mode === 'short_break') return s.shortBreakDuration * 60
  return s.longBreakDuration * 60
}

export const usePomodoroStore = create<PomodoroState>()(
  persist(
    (set, get) => ({
      mode: 'work',
      status: 'idle',
      timeRemaining: 25 * 60,
      endTime: null,
      currentSession: 1,
      startedAt: null,
      activeHabitId: null,
      activeHabitName: null,
      activeTaskId: null,
      activeTaskText: null,
      settings: DEFAULT_SETTINGS,
      isSettingsOpen: false,
      isBgPickerOpen: false,
      isSoundPickerOpen: false,
      isPipActive: false,

      startTimer: (): void => {
        const { mode, settings } = get()
        const duration = getDuration(mode, settings)
        set({
          status: 'running',
          timeRemaining: duration,
          endTime: Date.now() + duration * 1000,
          startedAt: new Date().toISOString(),
        })
      },

      pauseTimer: (): void => {
        set({ status: 'paused', endTime: null })
      },

      resumeTimer: (): void => {
        const { timeRemaining } = get()
        set({
          status: 'running',
          endTime: Date.now() + timeRemaining * 1000,
        })
      },

      skipToNext: (): void => {
        const { mode, currentSession, settings } = get()
        let nextMode: PomodoroMode
        let nextSession = currentSession

        if (mode === 'work') {
          nextSession = currentSession
          nextMode =
            currentSession % settings.sessionsBeforeLongBreak === 0
              ? 'long_break'
              : 'short_break'
        } else {
          nextSession = mode === 'long_break' ? 1 : currentSession + 1
          nextMode = 'work'
        }

        const duration = getDuration(nextMode, settings)
        set({
          mode: nextMode,
          status: 'idle',
          timeRemaining: duration,
          endTime: null,
          currentSession: nextSession,
          startedAt: null,
        })
      },

      resetAll: (): void => {
        const { settings } = get()
        set({
          mode: 'work',
          status: 'idle',
          timeRemaining: getDuration('work', settings),
          endTime: null,
          currentSession: 1,
          startedAt: null,
          activeHabitId: null,
          activeHabitName: null,
          activeTaskId: null,
          activeTaskText: null,
        })
      },

      tick: (): void => {
        const { status, endTime } = get()
        if (status !== 'running' || !endTime) return
        const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000))
        set({ timeRemaining: remaining })
      },

      recoverFromBackground: (): boolean => {
        const { status, endTime } = get()
        if (status !== 'running' || !endTime) return false
        const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000))
        set({ timeRemaining: remaining })
        return remaining <= 0
      },

      setMode: (mode: PomodoroMode): void => {
        const { settings, status } = get()
        if (status === 'running') return
        set({
          mode,
          timeRemaining: getDuration(mode, settings),
          endTime: null,
          status: 'idle',
        })
      },

      setActiveHabit: (id, name): void => {
        set({ activeHabitId: id, activeHabitName: name })
      },

      setActiveTask: (id, text): void => {
        set({ activeTaskId: id, activeTaskText: text })
      },

      updateSettings: (partial): void => {
        const { settings, status, mode } = get()
        const next = { ...settings, ...partial }
        const updates: Partial<PomodoroState> = { settings: next }
        if (status === 'idle') {
          updates.timeRemaining = getDuration(mode, next)
        }
        set(updates)
      },

      setSettingsOpen: (open) => { set({ isSettingsOpen: open }) },
      setBgPickerOpen: (open) => { set({ isBgPickerOpen: open }) },
      setSoundPickerOpen: (open) => { set({ isSoundPickerOpen: open }) },
      setPipActive: (active) => { set({ isPipActive: active }) },
      getDurationForMode: (mode): number => getDuration(mode, get().settings),
    }),
    {
      name: 'pomodoro-storage',
      partialize: (s) => ({
        mode: s.mode,
        status: s.status,
        timeRemaining: s.timeRemaining,
        endTime: s.endTime,
        currentSession: s.currentSession,
        startedAt: s.startedAt,
        activeHabitId: s.activeHabitId,
        activeHabitName: s.activeHabitName,
        activeTaskId: s.activeTaskId,
        activeTaskText: s.activeTaskText,
        settings: s.settings,
      }),
    },
  ),
)
